import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  TablePagination,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonOff as DeactivateIcon,
  PersonAdd as ActivateIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import { userService, UserListResponse } from '../../services/userService';
import { User } from '../../contexts/AuthContext';

interface UserManagementProps {
  onEditUser?: (user: User) => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({ onEditUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // États pour les actions utilisateur
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    action: () => {},
  });
  
  // États pour les notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Charger les utilisateurs
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1, // L'API utilise une pagination basée sur 1
        page_size: rowsPerPage,
        search: searchQuery || undefined,
        role: roleFilter || undefined,
        is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      };
      
      const response: UserListResponse = await userService.getAll(params);
      setUsers(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      showSnackbar('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, searchQuery, roleFilter, statusFilter]);

  // Afficher une notification
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  // Gérer la confirmation d'action
  const showConfirmDialog = (title: string, message: string, action: () => void) => {
    setConfirmDialog({ open: true, title, message, action });
  };

  // Désactiver un utilisateur
  const handleDeactivateUser = async (user: User) => {
    try {
      await userService.deactivate(user.id);
      showSnackbar(`Utilisateur ${user.username} désactivé avec succès`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la désactivation:', error);
      showSnackbar('Erreur lors de la désactivation de l\'utilisateur', 'error');
    }
  };

  // Réactiver un utilisateur
  const handleActivateUser = async (user: User) => {
    try {
      await userService.activate(user.id);
      showSnackbar(`Utilisateur ${user.username} réactivé avec succès`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la réactivation:', error);
      showSnackbar('Erreur lors de la réactivation de l\'utilisateur', 'error');
    }
  };

  // Supprimer définitivement un utilisateur
  const handleDeleteUser = async (user: User) => {
    try {
      await userService.delete(user.id);
      showSnackbar(`Utilisateur ${user.username} supprimé définitivement`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showSnackbar('Erreur lors de la suppression de l\'utilisateur', 'error');
    }
  };

  // Exporter les utilisateurs
  const handleExportUsers = async () => {
    try {
      const blob = await userService.exportUsers('csv');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `utilisateurs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSnackbar('Export des utilisateurs réussi', 'success');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      showSnackbar('Erreur lors de l\'export des utilisateurs', 'error');
    }
  };

  // Obtenir la couleur du chip selon le rôle
  const getRoleChipColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'veterinaire': return 'primary';
      case 'proprietaire': return 'secondary';
      default: return 'default';
    }
  };

  // Obtenir le libellé du rôle
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'veterinaire': return 'Vétérinaire';
      case 'proprietaire': return 'Propriétaire';
      default: return role;
    }
  };

  // Gérer le changement de page
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Gérer le changement du nombre de lignes par page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* En-tête avec filtres et actions */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" component="h1">
            Gestion des utilisateurs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
              disabled={loading}
            >
              Actualiser
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={handleExportUsers}
            >
              Exporter
            </Button>
          </Box>
        </Box>

        {/* Filtres */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Rechercher un utilisateur..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Rôle</InputLabel>
            <Select
              value={roleFilter}
              label="Rôle"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="">Tous les rôles</MenuItem>
              <MenuItem value="admin">Administrateur</MenuItem>
              <MenuItem value="veterinaire">Vétérinaire</MenuItem>
              <MenuItem value="proprietaire">Propriétaire</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Tous les statuts</MenuItem>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Table des utilisateurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom d'utilisateur</TableCell>
              <TableCell>Nom complet</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rôle</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Date d'inscription</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    Aucun utilisateur trouvé
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{`${user.first_name} ${user.last_name}`}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={getRoleLabel(user.role)}
                      color={getRoleChipColor(user.role)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.is_active ? 'Actif' : 'Inactif'}
                      color={user.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(user.date_inscription).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          onClick={() => onEditUser && onEditUser(user)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      {user.is_active ? (
                        <Tooltip title="Désactiver">
                          <IconButton
                            size="small"
                            onClick={() => showConfirmDialog(
                              'Désactiver l\'utilisateur',
                              `Êtes-vous sûr de vouloir désactiver ${user.username} ?`,
                              () => handleDeactivateUser(user)
                            )}
                          >
                            <DeactivateIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Réactiver">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => showConfirmDialog(
                              'Réactiver l\'utilisateur',
                              `Êtes-vous sûr de vouloir réactiver ${user.username} ?`,
                              () => handleActivateUser(user)
                            )}
                          >
                            <ActivateIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="Supprimer définitivement">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => showConfirmDialog(
                            'Supprimer l\'utilisateur',
                            `Êtes-vous sûr de vouloir supprimer définitivement ${user.username} ? Cette action est irréversible.`,
                            () => handleDeleteUser(user)
                          )}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </TableContainer>

      {/* Dialog de confirmation */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Annuler
          </Button>
          <Button 
            onClick={() => {
              confirmDialog.action();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            color="primary"
            variant="contained"
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 